import DigiobcanApi from '../../api/DigiobcanApi';
import { type CityRing, isInsideCity } from '../../lib/cityBoundary';
import { createQueryFn, QueryNameSpace, useQ } from '../../lib/query';
import React from 'react';

/**
 * Hranica mesta Prešov, podľa ktorej sa kreslí maska nad mapou a odmieta
 * poloha ešte pred odoslaním formulára.
 *
 * Je to statická geometria, nie údaj, ktorý sa mení - preto `staleTime`
 * nekonečno a jeden dotaz na celý beh appky. `Cache-Control` na endpointe robí
 * to isté pre ďalšie otvorenia stránky.
 *
 * Typ z vygenerovaného klienta je `Array<{}>`: vnorené pole dvojíc sa
 * v OpenAPI bez rekurzívneho `$ref` vyjadriť nedá a `$ref` s `description`
 * zhodí štart `koas3` na strane API. Preto sa tvar overí za behu a pri
 * čomkoľvek inom sa vráti `undefined` - appka potom beží bez masky, ako
 * predtým, namiesto toho, aby padla.
 */
export const getCityBoundary = createQueryFn(
  QueryNameSpace.getCityBoundary,
  async (): Promise<CityRing | undefined> => {
    const data = await DigiobcanApi.getCityBoundary();
    const [ring] = (data?.coordinates ?? []) as unknown[];

    if (
      !Array.isArray(ring) ||
      ring.length < 4 ||
      !ring.every(
        (p) =>
          Array.isArray(p) &&
          p.length >= 2 &&
          typeof p[0] === 'number' &&
          typeof p[1] === 'number',
      )
    ) {
      return undefined;
    }

    return ring.map(([lon, lat]) => [lon, lat] as [number, number]);
  },
);

/**
 * Hranica mesta plus hotová otázka „je tento bod v Prešove?".
 *
 * `isInside` je **fail-open**, kým hranica nie je načítaná alebo sa nepodarila
 * stiahnuť - tvrdú poistku drží API, toto je len to, aby používateľ nedostal
 * 400 až po odoslaní formulára.
 */
export const useCityBoundary = () => {
  const { data: ring } = useQ(getCityBoundary, undefined, {
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
  });

  const isInside = React.useCallback(
    (lon: number, lat: number) => isInsideCity(ring, lon, lat),
    [ring],
  );

  return { ring, isInside };
};
